// Frontend/src/api/announcements.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "./client";
import { useSsePollingFallbackActive } from "@/lib/useSseStatus";
import {
  Announcement,
  AnnouncementListItem,
  AnnouncementReach,
  CreateAnnouncementData,
  UpdateAnnouncementData,
} from "../types/announcement";

/**
 * List all announcements
 * - HOA Admins see announcements for their building
 * - Residents see only active announcements for their building
 * - Super Admins see all announcements
 */
export async function listAnnouncements(): Promise<AnnouncementListItem[]> {
  const res = await api.get<AnnouncementListItem[]>("/building/announcements/");
  return res.data;
}

/**
 * Get announcement details by ID
 */
export async function getAnnouncement(id: string): Promise<Announcement> {
  const res = await api.get<Announcement>(`/building/announcements/${id}/`);
  return res.data;
}

/**
 * Get only active announcements
 * Published and not expired
 */
export async function listActiveAnnouncements(): Promise<AnnouncementListItem[]> {
  const res = await api.get<AnnouncementListItem[]>(
    "/building/announcements/active/"
  );
  return res.data;
}

/**
 * Get only pinned announcements
 */
export async function listPinnedAnnouncements(): Promise<AnnouncementListItem[]> {
  const res = await api.get<AnnouncementListItem[]>(
    "/building/announcements/pinned/"
  );
  return res.data;
}

/**
 * Create a new announcement
 * Only HOA Admins and Super Admins can create
 */
export async function createAnnouncement(
  data: CreateAnnouncementData
): Promise<Announcement> {
  const res = await api.post<Announcement>("/building/announcements/", data);
  return res.data;
}

/**
 * Update an announcement
 * Only HOA Admins and Super Admins can update
 */
export async function updateAnnouncement(
  id: string,
  data: UpdateAnnouncementData
): Promise<Announcement> {
  const res = await api.put<Announcement>(`/building/announcements/${id}/`, data);
  return res.data;
}

/**
 * Delete an announcement
 * Only HOA Admins and Super Admins can delete
 */
export async function deleteAnnouncement(id: string): Promise<void> {
  await api.delete(`/building/announcements/${id}/`);
}

/**
 * How many people a broadcast to these buildings would actually reach.
 *
 * Two numbers, not one: residents who claimed their unit have a login and get
 * an in-app notification; the rest — 78% of them in production — have an
 * email address and nothing else. Shown on the compose form so "sent to 395
 * residents" is never claimed on behalf of 307 people who will only see mail.
 */
export async function getAnnouncementReach(
  buildingIds: string[]
): Promise<AnnouncementReach> {
  const res = await api.get<AnnouncementReach>("/building/announcements/reach/", {
    params: { buildings: buildingIds.join(",") },
  });
  return res.data;
}

// ==================== React Query Hooks ====================

export const ANNOUNCEMENTS_QUERY_KEY = "announcements";

/**
 * Fallback-only. A resident who already has this list open has their own
 * React Query cache; an admin publishing in a different browser only
 * invalidates the admin's cache, not the resident's. That gap is now closed
 * live: notificationStreamBridge.ts invalidates these queries the moment an
 * ANNOUNCEMENT_PUBLISHED event arrives over SSE, so this interval only
 * re-arms when useSsePollingFallbackActive() reports SSE itself is degraded.
 *
 * The admin's own list (useAnnouncements) does not receive that SSE event --
 * admins are not notification recipients for their own publish, by design --
 * but a create/edit already invalidates this query on mutation success
 * (see AnnouncementsPage.tsx), which covers the tab that did the publishing.
 * What this interval stops covering once SSE is healthy is a second tab of
 * the *same* admin; a manual refresh there is a small, rare cost next to
 * shipping a poll that never turns off.
 */
const FALLBACK_POLL_MS = 30_000;

/**
 * Hook to fetch all announcements
 */
export function useAnnouncements() {
  const pollingFallback = useSsePollingFallbackActive();
  return useQuery({
    queryKey: [ANNOUNCEMENTS_QUERY_KEY],
    queryFn: listAnnouncements,
    refetchInterval: pollingFallback ? FALLBACK_POLL_MS : false,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch active announcements only
 */
export function useActiveAnnouncements() {
  const pollingFallback = useSsePollingFallbackActive();
  return useQuery({
    queryKey: [ANNOUNCEMENTS_QUERY_KEY, "active"],
    queryFn: listActiveAnnouncements,
    refetchInterval: pollingFallback ? FALLBACK_POLL_MS : false,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch pinned announcements only
 */
export function usePinnedAnnouncements() {
  const pollingFallback = useSsePollingFallbackActive();
  return useQuery({
    queryKey: [ANNOUNCEMENTS_QUERY_KEY, "pinned"],
    queryFn: listPinnedAnnouncements,
    refetchInterval: pollingFallback ? FALLBACK_POLL_MS : false,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch announcement details
 */
export function useAnnouncement(id: string) {
  return useQuery({
    queryKey: [ANNOUNCEMENTS_QUERY_KEY, id],
    queryFn: () => getAnnouncement(id),
    enabled: !!id,
  });
}

/**
 * Hook for the reach line on the compose form.
 *
 * Disabled with no buildings selected: the backend would answer for every
 * building the admin owns, which is the right default for a bare call but the
 * wrong number to show against an empty selection.
 */
export function useAnnouncementReach(buildingIds: string[]) {
  return useQuery({
    queryKey: [ANNOUNCEMENTS_QUERY_KEY, "reach", [...buildingIds].sort()],
    queryFn: () => getAnnouncementReach(buildingIds),
    enabled: buildingIds.length > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook to create a new announcement
 */
export function useCreateAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => {
      // Invalidate all announcement queries to refetch
      queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENTS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update an announcement
 */
export function useUpdateAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAnnouncementData }) =>
      updateAnnouncement(id, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific announcement and the list
      queryClient.invalidateQueries({
        queryKey: [ANNOUNCEMENTS_QUERY_KEY, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENTS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to delete an announcement
 */
export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => {
      // Invalidate all announcement queries
      queryClient.invalidateQueries({ queryKey: [ANNOUNCEMENTS_QUERY_KEY] });
    },
  });
}
